

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Minus,
  Plus,
  Trash2,
  MapPin,
  ArrowRight,
  User,
  CreditCard,
  Truck,
  CheckCircle2,
  X,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { isLoggedIn } from "../utils/auth";
import { AuthAPI } from "../api/AuthAPI";

export default function CartPage() {
  const {
    cart,
    items,
    updateQty,
    incrementQty,
    decrementQty,
    removeFromCart,
    clearCart,
    fetchServerCart,
    closeCart,
  } = useCart();

  const navigate = useNavigate();

  const fetchingCartRef = useRef(false);

  // If the sidebar drawer was left open (e.g. header cart icon), it sits on
  // top of everything with a fixed full-screen overlay and eats clicks on
  // the header nav. Make sure it's closed the moment this page mounts.
  useEffect(() => {
    closeCart();
  }, []);

  /* ================= ADDRESS ================= */
  const [addresses, setAddresses] = useState([]);
  const [showAddresses, setShowAddresses] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [addressError, setAddressError] = useState("");

  const [addressForm, setAddressForm] = useState({
    address: "",
    pincode: "",
    city: "",
    phone: "",
    state: "",
    country: "India",
    is_default: true,
  });

  /* ================= ORDER NAME (standalone, not tied to address) ================= */
  const [orderName, setOrderName] = useState("");
  const [orderNameError, setOrderNameError] = useState("");
  const orderNameRef = useRef(null);

  /* ================= LOADING ================= */
  const [cartLoading, setCartLoading] = useState(true);

  /* ================= TOTALS ================= */
  const subtotal = Number(cart?.subtotal || 0);
  const discountAmount = Number(cart?.discount_amount || 0);
  const grandTotal = Number(cart?.grand_total || 0);

  /* ================= QTY (optimistic + debounced) ================= */
  const [localQty, setLocalQty] = useState({});
  const qtyDebounceRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(qtyDebounceRef.current).forEach(clearTimeout);
    };
  }, []);

  function getDisplayQty(item) {
    return localQty[item.id] ?? item.qty;
  }

  function changeQty(item, direction, stock) {
    const current = getDisplayQty(item);
    const next = direction === "inc" ? current + 1 : current - 1;

    if (next < 1) return;
    if (Number.isFinite(stock) && next > stock) return;

    setLocalQty((prev) => ({ ...prev, [item.id]: next }));

    if (qtyDebounceRef.current[item.id]) {
      clearTimeout(qtyDebounceRef.current[item.id]);
    }

    qtyDebounceRef.current[item.id] = setTimeout(async () => {
      await updateQty(item, next); // ONE call, with the final settled qty

      setLocalQty((prev) => {
        const copy = { ...prev };
        delete copy[item.id];
        return copy;
      });
    }, 500);
  }

  const firstOrderDiscount = {
    percentage: cart?.first_order_discount_config?.percentage || 0,
    status: cart?.first_order_discount_config?.status || null,
    used: cart?.first_order_discount_used ?? true,
  };

  const showFirstOrderBadge =
    firstOrderDiscount?.status === "active" &&
    !firstOrderDiscount?.used &&
    discountAmount > 0;

  /* ================= FETCH CART ================= */
  useEffect(() => {
    if (fetchingCartRef.current) return;
    fetchingCartRef.current = true;

    async function loadCart() {
      try {
        setCartLoading(true);
        await fetchServerCart();
      } catch (e) {
        console.error(e);
      } finally {
        setCartLoading(false);
      }
    }

    loadCart();

    return () => {
      fetchingCartRef.current = false;
    };
  }, []);

  /* ================= LOAD ADDRESS ================= */
  useEffect(() => {
    if (!isLoggedIn()) return;
    loadAddresses();
  }, []);

  async function loadAddresses() {
    try {
      const res = await AuthAPI.getaddress();
      const list = res?.data?.data || [];
      setAddresses(list);
      setSelectedAddress(list.find((a) => a.is_default) || list[0] || null);
    } catch (e) {
      console.error(e);
    }
  }

  /* ================= PAYMENT METHOD MODAL ================= */
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("online"); // "online" | "cod"
  const [placingOrder, setPlacingOrder] = useState(false);

  const hasAccountName = !!(cart?.user?.name && cart.user.name.trim() !== "");

  function getTrimmedName() {
    return hasAccountName ? cart.user.name.trim() : orderName.trim();
  }

  /* Validates name (mandatory) + address, then opens the payment method sheet.
     No order/payment API is called here anymore. */
  function handleOpenPaymentModal() {
    if (!isLoggedIn()) {
      window.dispatchEvent(new Event("open-login-popup"));
      return;
    }

    const trimmedName = getTrimmedName();
    if (!trimmedName) {
      setOrderNameError("Please enter your name to place the order.");
      orderNameRef.current?.focus();
      orderNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!selectedAddress) {
      alert("Please select address");
      return;
    }

    setPaymentMethod("online");
    setShowPaymentModal(true);
  }

  /* Branches on the selected payment method:
     - online -> existing createorder + Razorpay checkout flow
     - cod    -> a separate "cash on delivery" order endpoint, no Razorpay
     Both clear the cart on success and open the success popup, which
     lets the user navigate to /dashboard/purchase-history. */
  async function confirmOrder() {
    const trimmedName = getTrimmedName();
    setPlacingOrder(true);

    try {
      if (paymentMethod === "online") {
        const res = await AuthAPI.createorder({
          address_id: selectedAddress.id,
          cart_id: cart?.id,
          user_name: trimmedName,
        });

        const orderData = res?.data?.data;

        const options = {
          key: orderData.key,
          amount: orderData.amount,
          currency: "INR",
          order_id: orderData.razorpay_order_id,

        handler: async function (response) {
  try {
    await AuthAPI.verifypayment({
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
    });

    localStorage.removeItem("guest_cart");
    localStorage.removeItem("cart_items");

    clearCart();
    setShowPaymentModal(false);
    setPlacingOrder(false);
    navigate("/order-success", { state: { method: "online" } });
  } catch (err) {
    console.error("verifypayment failed:", err);
    alert("Payment verification failed. Please contact support with your payment ID.");
    setPlacingOrder(false);
  }
},

          modal: {
            // Razorpay checkout closed without paying — re-enable the button
            ondismiss: function () {
              setPlacingOrder(false);
            },
          },

          prefill: {
            contact: selectedAddress.phone,
            name: trimmedName,
          },

          theme: {
            color: "#facc15",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // TODO: rename AuthAPI.createCodOrder to whatever your COD endpoint is
        await AuthAPI.createCodOrder({
          address_id: selectedAddress.id,
          cart_id: cart?.id,
          user_name: trimmedName,
          payment_method: "cod",
        });

        localStorage.removeItem("guest_cart");
        localStorage.removeItem("cart_items");

        clearCart();
        setShowPaymentModal(false);
        setPlacingOrder(false);
        navigate("/order-success", { state: { method: "cod" } });
      }
    } catch (err) {
      console.error(err);
      alert("Order failed");
      setPlacingOrder(false);
    }
  }

  /* ================= HELPERS ================= */
  function validatePhone(phone) {
    return /^[0-9]{10}$/.test(phone);
  }

  async function fetchCityStateFromPincode(pincode) {
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      if (!res.ok) throw new Error("Network error");

      const data = await res.json();
      const result = data?.[0];

      if (
        result?.Status !== "Success" ||
        !Array.isArray(result?.PostOffice) ||
        result.PostOffice.length === 0
      ) {
        return null;
      }

      const po = result.PostOffice[0];
      return { city: po.District || po.Name || "", state: po.State || "" };
    } catch (error) {
      return null;
    }
  }

  async function handleCartPostalChange(pincode) {
    const value = pincode.replace(/\D/g, "");
    setAddressForm((f) => ({ ...f, pincode: value }));

    if (value.length !== 6) {
      setAddressForm((f) => ({ ...f, city: "", state: "" }));
      return;
    }

    const location = await fetchCityStateFromPincode(value);

    if (!location) {
      setAddressForm((f) => ({ ...f, city: "", state: "" }));
      return;
    }

    setAddressForm((f) => ({ ...f, city: location.city, state: location.state }));
  }

  /* ================= EMPTY STATE ================= */
  if (!cartLoading && items.length === 0) {
    return (
      <main className="min-h-screen bg-gradient-to-b from-[#fff7fb] via-white to-[#f5f7ff] px-4 py-16 mt-10 md:mt-0">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-6xl mb-4">🛒</p>
          <h2 className="text-2xl font-bold text-[#1A237E]">Your cart feels lonely</h2>
          <p className="text-gray-500 mt-2">Add products you love and make your shopping amazing.</p>
          <Link
            to="/products"
            className="mt-6 inline-block px-6 py-3 rounded-full bg-pink-500 text-white font-semibold hover:bg-pink-600 transition"
          >
            Continue Shopping
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#fff7fb] via-white to-[#f5f7ff] px-4 py-8 md:py-12 mt-10 md:mt-0">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-playfair text-[28px] md:text-[38px] font-bold text-[#1A237E] mb-8">
          Your Cart
        </h1>

        {/* FIRST ORDER BANNER */}
        {firstOrderDiscount?.status === "active" && !firstOrderDiscount?.used && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-sm text-green-700 mb-6">
            🎉 First order discount available <strong>{firstOrderDiscount.percentage}% OFF</strong>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* ================= LEFT: ITEMS ================= */}
          <div className="lg:col-span-2 space-y-4">
            {/* SHIMMER */}
            {cartLoading &&
              [1, 2, 3].map((i) => (
                <div key={i} className="border border-pink-100 rounded-2xl p-4 bg-white animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-24 h-24 bg-gray-200 rounded-xl" />
                    <div className="flex-1 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-1/3" />
                      <div className="h-10 bg-gray-200 rounded w-32" />
                    </div>
                  </div>
                </div>
              ))}

            {/* ITEMS */}
            {!cartLoading &&
              items.map((item) => {
                const product = item?.product || {};
                const galleryImages =
                  product?.images?.length > 0
                    ? product.images.map((img) => img.image)
                    : item?.image
                    ? [item.image]
                    : ["/placeholder.png"];
                const mainImage = item?.image || galleryImages[0] || "/placeholder.png";

                const unitPrice = Number(item?.unit_price || 0);
                const stock = product?.stock ?? Infinity;
                const lowStock = Number.isFinite(stock) && stock <= 5;

                return (
                  <motion.div
                    key={item.id}
                    className="flex gap-4 p-5 sm:p-6 rounded-2xl bg-white border border-pink-100 shadow-[0_10px_40px_rgba(236,72,153,0.06)]"
                  >
                    <div className="flex-shrink-0">
                      <img
                        src={mainImage}
                        alt={item?.product_name}
                        className="w-16 h-16 sm:w-24 sm:h-24 rounded-xl object-cover"
                      />

                      {galleryImages.length > 1 && (
                        <div className="hidden sm:flex gap-1 mt-2">
                          {galleryImages.slice(1, 3).map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt=""
                              className="w-6 h-6 rounded-md object-cover border border-pink-100"
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <p className="font-semibold text-sm leading-5 line-clamp-2">
                        {item?.product_name}
                      </p>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        {item?.sku && (
                          <p className="text-xs text-gray-400">SKU: {item.sku}</p>
                        )}
                        <span className="text-sm text-gray-500">
                          Unit Price: <span className="font-medium text-gray-700">₹{unitPrice}</span>
                        </span>
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <span className="text-sm text-gray-500">
                          Qty: <span className="font-medium text-gray-700">{getDisplayQty(item)}</span>
                        </span>
                        Total:<span className="font-bold text-lg">₹{(unitPrice * getDisplayQty(item)).toFixed(2)}</span>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        {Number.isFinite(stock) && (
                          <span
                            className={`flex items-center gap-1 text-xs ${
                              lowStock ? "text-orange-500" : "text-gray-400"
                            }`}
                          />
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-3 mt-4">
                        <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden shrink-0">
                          <button
                            onClick={() => changeQty(item, "dec", stock)}
                            disabled={getDisplayQty(item) <= 1}
                            className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-pink-50 transition disabled:opacity-40 disabled:hover:bg-transparent"
                          >
                            <Minus size={15} />
                          </button>
                          <span className="w-9 text-center font-semibold text-sm border-x border-gray-200">
                            {getDisplayQty(item)}
                          </span>
                          <button
                            onClick={() => changeQty(item, "inc", stock)}
                            disabled={getDisplayQty(item) >= stock}
                            className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-pink-50 transition disabled:opacity-40 disabled:hover:bg-transparent"
                          >
                            <Plus size={15} />
                          </button>
                        </div>

                        <button
                          onClick={() => removeFromCart(item)}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-red-500 border border-red-200 hover:bg-red-50 transition text-sm font-medium shrink-0"
                        >
                          <Trash2 size={15} />
                          <span className="hidden sm:inline">Remove</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
          </div>

          {/* ================= RIGHT: NAME + ADDRESS + SUMMARY ================= */}
          <div className="space-y-6 h-fit lg:sticky lg:top-20">
            {/* ================= YOUR NAME (mandatory) ================= */}
            {cart?.user_id && (
              <div className="border border-pink-100 rounded-2xl bg-white overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-pink-50">
                  <div className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center">
                    <User size={16} className="text-pink-500" />
                  </div>
                  <span className="font-semibold text-[15px]">Ordering As</span>
                  {!hasAccountName && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-red-500">
                      Required
                    </span>
                  )}
                </div>

                <div className="p-5">
                  {hasAccountName ? (
                    <div>
                      <p className="text-xs text-gray-400">Name</p>
                      <p className="text-sm font-semibold text-gray-800">{cart.user.name}</p>
                    </div>
                  ) : (
                    <>
                      <label className="text-xs font-medium text-gray-500 mb-1.5 block">
                        Full name for this order *
                      </label>
                      <input
                        ref={orderNameRef}
                        type="text"
                        value={orderName}
                        onChange={(e) => {
                          setOrderName(e.target.value);
                          if (orderNameError) setOrderNameError("");
                        }}
                        placeholder="Enter your full name"
                        className={`w-full border rounded-xl p-3 text-sm outline-none transition ${
                          orderNameError
                            ? "border-red-400 focus:border-red-500"
                            : "border-gray-200 focus:border-pink-400"
                        }`}
                      />
                      {orderNameError && (
                        <p className="text-red-500 text-xs mt-2">{orderNameError}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ================= ADDRESS (Amazon-style, labeled) ================= */}
            <div className="border border-pink-100 rounded-2xl bg-white overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-pink-50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center">
                    <MapPin size={16} className="text-pink-500" />
                  </div>
                  <span className="font-semibold text-[15px]">Delivery Address</span>
                </div>
                {isLoggedIn() && addresses.length > 0 && (
                  <button
                    onClick={() => setShowAddresses(!showAddresses)}
                    className="text-xs font-semibold text-pink-600 hover:underline"
                  >
                    {showAddresses ? "Hide" : "Change"}
                  </button>
                )}
              </div>

              <div className="p-5">
                {!isLoggedIn() && (
                  <button
                    onClick={() => window.dispatchEvent(new Event("open-login-popup"))}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 py-3 rounded-xl font-semibold transition"
                  >
                    Login to select address
                  </button>
                )}

                {isLoggedIn() && selectedAddress && !showAddresses && (
                  <div className="relative rounded-2xl p-4 bg-gradient-to-br from-pink-50 to-white border border-pink-200">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-pink-500 flex items-center justify-center shrink-0">
                        <CheckCircle2 size={18} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm text-gray-800">Deliver here</p>
                          {selectedAddress.is_default && (
                            <span className="text-[10px] font-bold uppercase tracking-wide bg-pink-500 text-white px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </div>

                        {selectedAddress.phone && (
                          <p className="text-xs text-gray-500 mt-2">
                            <span className="font-medium text-gray-700">Phone:</span>{" "}
                            {selectedAddress.phone}
                          </p>
                        )}
                        <p className="text-sm text-gray-600 mt-1 leading-5">
                          <span className="font-medium text-gray-700">Address:</span>{" "}
                          {selectedAddress.address_line_1 || selectedAddress.address}, {selectedAddress.city},{" "}
                          {selectedAddress.state} - {selectedAddress.pincode}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {isLoggedIn() && addresses.length === 0 && !showAddresses && (
                  <button
                    onClick={() => {
                      setShowAddresses(true);
                      setShowAddAddressForm(true);
                    }}
                    className="w-full border-2 border-dashed border-gray-300 rounded-2xl py-4 text-gray-500 hover:border-pink-400 hover:text-pink-600 font-medium text-sm transition"
                  >
                    + Add Delivery Address
                  </button>
                )}

                {showAddresses && (
                  <div className="space-y-3">
                    {addresses.map((addr) => {
                      const isSelected = selectedAddress?.id === addr.id;
                      return (
                        <div
                          key={addr.id}
                          onClick={() => setSelectedAddress(addr)}
                          className={`flex items-start gap-3 border rounded-2xl p-4 cursor-pointer transition ${
                            isSelected
                              ? "border-pink-400 bg-pink-50 ring-1 ring-pink-300"
                              : "border-gray-200 hover:border-pink-200"
                          }`}
                        >
                          <span
                            className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                              isSelected ? "border-pink-500" : "border-gray-300"
                            }`}
                          >
                            {isSelected && <span className="w-2 h-2 rounded-full bg-pink-500" />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-sm">Address</p>
                              {addr.is_default && (
                                <span className="text-[10px] font-bold uppercase tracking-wide bg-pink-500 text-white px-2 py-0.5 rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                            {addr.phone && (
                              <p className="text-xs text-gray-500 mt-1">
                                <span className="font-medium text-gray-700">Phone:</span> {addr.phone}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium text-gray-700">Address:</span>{" "}
                              {addr.address_line_1 || addr.address}, {addr.city}, {addr.state} - {addr.pincode}
                            </p>
                          </div>
                        </div>
                      );
                    })}

                    <button
                      onClick={() => setShowAddAddressForm(!showAddAddressForm)}
                      className="w-full border-2 border-dashed border-gray-300 rounded-2xl py-4 text-gray-500 hover:border-pink-400 hover:text-pink-600 font-medium text-sm transition"
                    >
                      + Add New Address
                    </button>

                    {showAddAddressForm && (
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          try {
                            if (addressError) return;

                            const payload = {
                              address: addressForm.address,
                              pincode: addressForm.pincode,
                              city: addressForm.city,
                              state: addressForm.state,
                              phone: addressForm.phone,
                              country: "India",
                              is_default: true,
                            };

                            await AuthAPI.createaddress(payload);
                            const res = await AuthAPI.getaddress();
                            const list = res?.data?.data || [];

                            setAddresses(list);
                            setSelectedAddress(list[list.length - 1] || list[0]);
                            setShowAddAddressForm(false);
                            setShowAddresses(true);

                            setAddressForm({
                              address: "",
                              pincode: "",
                              city: "",
                              phone: "",
                              state: "",
                              country: "India",
                              is_default: true,
                            });
                          } catch (err) {
                            console.error(err);
                            alert("Failed to save address");
                          }
                        }}
                        className="border border-gray-200 rounded-2xl p-4 bg-gray-50 space-y-4"
                      >
                        <p className="font-semibold text-sm text-[#1A237E]">New Delivery Address</p>

                        <div>
                          <label className="text-sm font-medium mb-1 block">Full Address</label>
                          <textarea
                            placeholder="House no., street, locality, landmark"
                            className="w-full border rounded-xl p-3 outline-none focus:border-pink-400 bg-white"
                            rows={3}
                            value={addressForm.address}
                            onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                            required
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium mb-1 block">Pincode</label>
                            <input
                              type="text"
                              placeholder="400001"
                              maxLength={6}
                              inputMode="numeric"
                              className="w-full border rounded-xl p-3 outline-none focus:border-pink-400"
                              value={addressForm.pincode}
                              onChange={(e) => handleCartPostalChange(e.target.value)}
                              required
                            />
                          </div>

                          <div>
                            <label className="text-sm font-medium mb-1 block">Phone</label>
                            <input
                              type="text"
                              placeholder="9876543210"
                              maxLength={10}
                              className={`w-full border rounded-xl p-3 outline-none ${
                                addressError ? "border-red-500" : "focus:border-pink-400"
                              }`}
                              value={addressForm.phone}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                setAddressForm({ ...addressForm, phone: value });

                                if (value.length === 0) setAddressError("");
                                else if (!validatePhone(value))
                                  setAddressError("Phone number must be exactly 10 digits");
                                else setAddressError("");
                              }}
                              required
                            />
                            {addressError && <p className="text-red-500 text-xs mt-1">{addressError}</p>}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm font-medium mb-1 block">City</label>
                            <input
                              type="text"
                              value={addressForm.city}
                              onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                              placeholder="Enter city"
                              className="w-full border rounded-xl p-3 outline-none focus:border-pink-400"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">State</label>
                            <input
                              type="text"
                              value={addressForm.state}
                              onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                              placeholder="Enter state"
                              className="w-full border rounded-xl p-3 outline-none focus:border-pink-400"
                            />
                          </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                          <button
                            type="button"
                            onClick={() => setShowAddAddressForm(false)}
                            className="flex-1 border border-gray-300 py-3 rounded-xl font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={!!addressError}
                            className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-3 rounded-xl font-semibold transition"
                          >
                            Save Address
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* PRICE SUMMARY */}
            <div className="border border-pink-100 rounded-2xl bg-white p-5">
              <h3 className="font-bold text-lg mb-5">Price Details</h3>

              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Items</span>
                  <span className="font-medium">{cart?.total_qty || 0}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Total MRP</span>
                  <span className="font-medium">₹{subtotal.toFixed(2)}</span>
                </div>

                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Coupon Discount</span>
                    <span>- ₹{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping Charges</span>
                  <span className="font-medium">
                    {cart?.shipping_charges > 0 ? `₹${Number(cart.shipping_charges).toFixed(2)}` : "Free"}
                  </span>
                </div>

                <div className="border-t pt-4 flex justify-between items-center">
                  <div>
                    <p className="font-bold text-lg">₹{grandTotal.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Grand Total</p>
                  </div>
                </div>

                {showFirstOrderBadge && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-green-700 text-sm">
                    🎉 First Order Discount Applied
                  </div>
                )}

                <button
                  onClick={handleOpenPaymentModal}
                  className="w-full mt-2 flex items-center justify-center gap-2 bg-pink-500 hover:bg-pink-600 text-white transition px-8 py-3 rounded-full font-bold"
                >
                  Place Order <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= PAYMENT METHOD MODAL ================= */}
      <AnimatePresence>
        {showPaymentModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !placingOrder && setShowPaymentModal(false)}
              className="fixed inset-0 bg-black/40 z-[100]"
            />

            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 md:inset-0 md:flex md:items-center md:justify-center z-[101]"
            >
              <div className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl p-6 md:p-7 max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-lg text-[#1A237E]">Select Payment Method</h3>
                  <button
                    onClick={() => !placingOrder && setShowPaymentModal(false)}
                    className="p-1.5 rounded-full hover:bg-gray-100 transition"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-3">
                  <div
                    onClick={() => setPaymentMethod("online")}
                    className={`flex items-start gap-3 border rounded-2xl p-4 cursor-pointer transition ${
                      paymentMethod === "online"
                        ? "border-pink-400 bg-pink-50 ring-1 ring-pink-300"
                        : "border-gray-200 hover:border-pink-200"
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        paymentMethod === "online" ? "border-pink-500" : "border-gray-300"
                      }`}
                    >
                      {paymentMethod === "online" && <span className="w-2 h-2 rounded-full bg-pink-500" />}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CreditCard size={16} className="text-pink-500" />
                        <p className="font-semibold text-sm">Online Payment</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Pay securely via UPI, Card, Netbanking &amp; Wallets
                      </p>
                    </div>
                  </div>

                  <div
                    onClick={() => setPaymentMethod("cod")}
                    className={`flex items-start gap-3 border rounded-2xl p-4 cursor-pointer transition ${
                      paymentMethod === "cod"
                        ? "border-pink-400 bg-pink-50 ring-1 ring-pink-300"
                        : "border-gray-200 hover:border-pink-200"
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        paymentMethod === "cod" ? "border-pink-500" : "border-gray-300"
                      }`}
                    >
                      {paymentMethod === "cod" && <span className="w-2 h-2 rounded-full bg-pink-500" />}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Truck size={16} className="text-pink-500" />
                        <p className="font-semibold text-sm">Cash on Delivery</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Pay with cash when your order arrives</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 mt-5 pt-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Order Total</p>
                    <p className="font-bold text-lg text-[#1A237E]">₹{grandTotal.toFixed(2)}</p>
                  </div>
                  <button
                    onClick={confirmOrder}
                    disabled={placingOrder}
                    className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white transition px-6 py-3 rounded-full font-bold text-sm shrink-0"
                  >
                    {placingOrder ? "Placing Order..." : paymentMethod === "online" ? "Proceed to Pay" : "Place Order"}
                    {!placingOrder && <ArrowRight size={16} />}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </main>
  );
}